import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  User,
  CreditCard,
  Settings,
  LogOut,
  Phone,
  MapPin,
  Camera,
  Star,
  Bell,
  PenLine,
  CalendarDays,
  BookOpen,
  Bookmark,
} from 'lucide-react';
import { Link, useLocation } from 'wouter';
import { validatePassword } from '@shared/validation';

import { useAuth, type AuthUser } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { queryClient, apiRequest, ApiError } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { PublicShell } from '@/components/public-shell';
import { WriterPortal } from '@/components/writer-portal';
import { authedFetch } from '@/lib/http';
import { logoutAndRedirect } from '@/lib/logout';
import { formatPhoneNumber, extractPhoneDigits } from '@/lib/phoneFormat';
import { formatDateCompact } from '@/lib/dateUtils';
import { formatSessionTime } from '@/lib/geography';
import { useGeographyContext } from '@/hooks/useGeographyContext';
import { SendPunchesDialog, SentTransfersList } from '@/components/pass-transfers';
import {
  canRequestEnrollmentCancellation,
  canCancelPassEnrollment,
  shouldEmailTeacherForIssue,
  formatEnrollmentStatusLabel,
  getEnrollmentDashboardGroups,
  type DashboardEnrollmentRecord,
} from '@/lib/enrollment-dashboard';

const US_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Indiana/Indianapolis', label: 'Indiana — Eastern, no DST' },
  { value: 'America/Phoenix', label: 'Arizona — Mountain, no DST' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'UTC', label: 'UTC' },
];

// Type definitions for enrollment data
interface EnrollmentData extends DashboardEnrollmentRecord {
  id: string;
  orderId?: string | null;
  classId?: string;
  sessionId?: string | null;
  isPassEnrollment?: boolean | null;
  status?: string;
  orderStatus?: string | null;
  orderTotal?: number;
  quantity?: number;
  purchaseDate?: string;
  enrolledAt?: string;
  cancellationPolicy?: string | null;
  refundRequestStatus?: string | null;
  refundDeniedReason?: string | null;
  classThumbnailUrl?: string;
  classTitle?: string;
  creatorName?: string;
  sessionStartDateTime?: string;
  sessionEndDateTime?: string;
  sessionMaxStudents?: number;
  sessionNotes?: string;
  classDescription?: string;
  classDuration?: number;
  reminderEnabled?: boolean;
}

interface CreatorApplicationStatus {
  id: string;
  status: string;
  adminNotes?: string | null;
  createdAt: string;
}

export default function Account() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [location] = useLocation();
  const { locality: activeLocality, region: activeRegion } = useGeographyContext();
  const sessionTimezone =
    activeLocality?.timezone ?? activeRegion?.timezone ?? 'America/Indiana/Indianapolis';

  // Helper function to format the full address
  const formatFullAddress = (user: {
    streetAddress?: string | null;
    addressLine2?: string | null;
    city?: string | null;
    state?: string | null;
    zipCode?: string | null;
  }) => {
    const parts = [];
    if (user.streetAddress) parts.push(user.streetAddress);
    if (user.addressLine2) parts.push(user.addressLine2);
    if (user.city) parts.push(user.city);
    if (user.state) parts.push(user.state);
    if (user.zipCode) parts.push(user.zipCode);

    if (parts.length === 0) return null;

    // Format as: "Street Address, Address Line 2, City, State ZipCode"
    const streetPart = [user.streetAddress, user.addressLine2].filter(Boolean).join(', ');
    const cityStatePart = [user.city, user.state].filter(Boolean).join(', ');
    const locationPart = [cityStatePart, user.zipCode].filter(Boolean).join(' ');

    return [streetPart, locationPart].filter(Boolean).join(', ');
  };
  const [editingProfile, setEditingProfile] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [uploadingProfilePicture, setUploadingProfilePicture] = useState(false);
  const [profileImageFailed, setProfileImageFailed] = useState(false);
  const [profileForm, setProfileForm] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phoneNumber: formatPhoneNumber(user?.phoneNumber || ''),
    streetAddress: user?.streetAddress || '',
    addressLine2: user?.addressLine2 || '',
    city: user?.city || '',
    state: user?.state || '',
    zipCode: user?.zipCode || '',
    country: user?.country || 'US',
    timezone: user?.timezone && user.timezone !== 'UTC' ? user.timezone : 'America/New_York',
    hasCompletedOnboarding: user?.hasCompletedOnboarding || false,
    emailEnrollmentConfirmation: user?.emailEnrollmentConfirmation ?? true,
    emailClassReminders: user?.emailClassReminders ?? true,
    emailCreatorUpdates: user?.emailCreatorUpdates ?? true,
    emailAccountNotifications: user?.emailAccountNotifications ?? true,
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // Fetch user enrollments for order history
  const { data: enrollments = [], isLoading: enrollmentsLoading } = useQuery<EnrollmentData[]>({
    queryKey: ['/api/enrollments'],
    enabled: !!user,
  });

  // Fetch personal calendar (enrollments + saved/RSVP'd events)
  type MyCalendarItem = {
    type: 'enrollment' | 'going' | 'interested' | 'saved';
    id: string;
    classId?: string;
    sessionId?: string;
    title: string;
    creatorName: string | null;
    startsAt: string;
    endsAt: string | null;
    status?: string;
    pricingMode?: string;
    deliveryMode?: string;
    category?: string;
    externalUrl?: string | null;
    attendanceMode?: string;
    location: string | null;
  };
  const { data: myCalendar = [], isLoading: calendarLoading } = useQuery<MyCalendarItem[]>({
    queryKey: ['/api/calendar/mine'],
    enabled: !!user,
  });

  // Fetch active punch passes
  const { data: passesData } = useQuery<{
    passes: Array<{
      id: string;
      classId: string;
      productName: string | null;
      totalSessions: number;
      usedSessions: number;
      remainingSessions: number;
      expiresAt: string | null;
      createdAt: string;
      // Every class the pass can be used on (empty for legacy class-only passes).
      coveredClasses: Array<{ id: string; title: string }>;
    }>;
  }>({
    queryKey: ['/api/classes/user/passes'],
    enabled: !!user,
  });

  // Creator application state
  const [applyForm, setApplyForm] = useState({
    displayName: '',
    phoneNumber: '',
    bio: '',
    creatorHeadline: '',
    portfolioUrl: '',
    experience: '',
    proposedContent: '',
    experienceLevel: '',
    hasExistingAudience: false,
    audienceDescription: '',
    primaryLocation: '',
    referralSource: '',
  });
  const [showApplyForm, setShowApplyForm] = useState(false);
  // Pass whose "Send punches" dialog is open.
  const [sendingFromPass, setSendingFromPass] = useState<{
    id: string;
    productName: string | null;
    remainingSessions: number;
  } | null>(null);

  const { data: creatorApplication, refetch: refetchApplication } =
    useQuery<CreatorApplicationStatus | null>({
      queryKey: ['/api/creator/applications/current'],
      enabled: !!user && user.role === 'user',
    });

  const submitApplicationMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      return await apiRequest('POST', '/api/creator/applications', data);
    },
    onSuccess: () => {
      toast({
        title: 'Application Submitted',
        description: 'Your creator application has been submitted for review.',
      });
      setShowApplyForm(false);
      refetchApplication();
    },
    onError: (error: Error) => {
      toast({
        title: 'Submission Failed',
        description: error.message || 'Failed to submit application.',
        variant: 'destructive',
      });
    },
  });

  // Determine if user uses email/password authentication
  const isEmailPasswordUser = user?.hasPassword === true;

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      return await apiRequest('PUT', '/api/auth/profile', data);
    },
    onSuccess: () => {
      toast({
        title: 'Profile Updated',
        description: 'Your profile has been successfully updated.',
      });
      setEditingProfile(false);
      queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });

      // If user is a creator, also invalidate creator profile queries
      if (user?.role === 'creator' && user?.id) {
        queryClient.invalidateQueries({ queryKey: ['/api/creators', user.id.toString()] });
        queryClient.invalidateQueries({ queryKey: ['/api/creators'] });
      }
    },
    onError: _error => {
      toast({
        title: 'Update Failed',
        description: 'Failed to update profile. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const updateEmailPreferencesMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest('PUT', '/api/auth/profile', {
        emailEnrollmentConfirmation: profileForm.emailEnrollmentConfirmation,
        emailClassReminders: profileForm.emailClassReminders,
        emailCreatorUpdates: profileForm.emailCreatorUpdates,
        emailAccountNotifications: profileForm.emailAccountNotifications,
      });
    },
    onSuccess: () => {
      toast({
        title: 'Email Preferences Updated',
        description: 'Your email settings have been saved.',
      });
      queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
    },
    onError: () => {
      toast({
        title: 'Update Failed',
        description: 'Failed to update email preferences. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Extract only digits from phone number for storage
    const formDataToSubmit = {
      ...profileForm,
      phoneNumber: extractPhoneDigits(profileForm.phoneNumber),
    };
    updateProfileMutation.mutate(formDataToSubmit);
  };

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: async (data: { currentPassword: string; newPassword: string }) => {
      return await apiRequest('POST', '/api/auth/change-password', data);
    },
    onSuccess: () => {
      toast({
        title: 'Password Changed',
        description: 'Your password has been successfully updated.',
      });
      setChangingPassword(false);
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    },
    onError: error => {
      // Surface the server's specific reason (e.g. wrong current password,
      // password complexity) instead of always blaming the current password.
      let description = 'Failed to change password. Please try again.';
      if (error instanceof ApiError) {
        try {
          const parsed = JSON.parse(error.body) as { error?: string };
          if (parsed?.error) description = parsed.error;
        } catch {
          // non-JSON body — keep the generic message
        }
      }
      toast({ title: 'Change Failed', description, variant: 'destructive' });
    },
  });

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast({
        title: 'Password Mismatch',
        description: 'New password and confirmation do not match.',
        variant: 'destructive',
      });
      return;
    }

    // Mirror the server's complexity rule (validatePassword) so the user gets
    // accurate feedback before submitting instead of a generic server rejection.
    if (!validatePassword(passwordForm.newPassword)) {
      toast({
        title: 'Password Too Weak',
        description:
          'Use at least 8 characters with an uppercase letter, a lowercase letter, a number, and a special character.',
        variant: 'destructive',
      });
      return;
    }

    // Send only the fields the API accepts — the schema is strict and rejects
    // unknown keys (e.g. confirmPassword).
    changePasswordMutation.mutate({
      currentPassword: passwordForm.currentPassword,
      newPassword: passwordForm.newPassword,
    });
  };

  const handleProfilePictureUpload = async (file: File) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid File Type',
        description: 'Please select an image file.',
        variant: 'destructive',
      });
      return;
    }

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: 'File Too Large',
        description: 'Please select an image smaller than 10MB.',
        variant: 'destructive',
      });
      return;
    }

    setUploadingProfilePicture(true);

    try {
      // Upload and optimize image using new R2 endpoint
      const response = await authedFetch('/api/upload/profile-picture', {
        method: 'POST',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Upload failed');
      }

      const { imageUrl } = (await response.json()) as { imageUrl: string; message: string };

      toast({
        title: 'Success',
        description: 'Profile picture updated successfully!',
      });

      // Immediately apply new URL to cached user so the image updates without a round-trip
      setProfileImageFailed(false);
      queryClient.setQueryData<AuthUser | null>(['/api/auth/user'], old =>
        old ? { ...old, profileImageUrl: imageUrl } : old
      );
      void queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });

      // If user is a creator, also invalidate creator profile queries
      if (user?.role === 'creator' && user?.id) {
        void queryClient.invalidateQueries({ queryKey: ['/api/creators', user.id.toString()] });
        void queryClient.invalidateQueries({ queryKey: ['/api/creators'] });
      }
    } catch (error) {
      toast({
        title: 'Upload Failed',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to upload profile picture. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setUploadingProfilePicture(false);
    }
  };

  const handleLogout = async () => {
    await logoutAndRedirect('/');
  };

  if (!user) {
    return null;
  }

  const showingEnrollmentsRoute = location.startsWith('/account/enrollments');
  const defaultTab = showingEnrollmentsRoute ? 'classes' : 'profile';
  const enrollmentGroups = getEnrollmentDashboardGroups(enrollments);
  const nextUpcomingEnrollment = enrollmentGroups.upcoming[0] ?? null;
  const activeMarketLabel = activeLocality?.name ?? activeRegion?.name ?? 'Online';

  return (
    <PublicShell routeKey={showingEnrollmentsRoute ? 'account-enrollments' : 'account'}>
      <div className="page-shell py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold">Account Management</h1>
          <p className="text-muted-foreground mt-2">
            Keep upcoming classes, past bookings, refund status, and account preferences in one
            place.
          </p>
        </div>

        <div className="mb-8 grid gap-4 sm:grid-cols-3">
          <AccountOverviewCard
            icon={CreditCard}
            label="Upcoming classes"
            value={String(enrollmentGroups.upcoming.length)}
            detail={
              nextUpcomingEnrollment?.sessionStartDateTime
                ? formatDateCompact(nextUpcomingEnrollment.sessionStartDateTime)
                : 'Nothing scheduled yet'
            }
          />
          <AccountOverviewCard
            icon={Settings}
            label="Past history"
            value={String(enrollmentGroups.past.length)}
            detail={
              enrollmentGroups.past.length > 0
                ? 'Completed or older bookings'
                : 'No class history yet'
            }
          />
          <AccountOverviewCard
            icon={MapPin}
            label="Active market"
            value={activeMarketLabel}
            detail="Used for local browsing and class recommendations"
          />
        </div>

        <Tabs defaultValue={defaultTab} className="space-y-6">
          <div className="overflow-x-auto overscroll-x-contain">
            <TabsList className="inline-flex w-max">
              <TabsTrigger value="profile" className="flex items-center gap-2">
                <User size={16} />
                Profile
              </TabsTrigger>
              <TabsTrigger value="classes" className="flex items-center gap-2">
                <CreditCard size={16} />
                Classes
              </TabsTrigger>
              <TabsTrigger value="passes" className="flex items-center gap-2">
                <Star size={16} />
                Passes
              </TabsTrigger>
              {user.isBlogWriter && (
                <TabsTrigger value="writing" className="flex items-center gap-2">
                  <PenLine size={16} />
                  Writing
                </TabsTrigger>
              )}
              <TabsTrigger value="calendar" className="flex items-center gap-2">
                <CalendarDays size={16} />
                My Calendar
              </TabsTrigger>
              <TabsTrigger value="settings" className="flex items-center gap-2">
                <Settings size={16} />
                Settings
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="profile">
            <Card data-testid="profile-card">
              <CardHeader>
                <CardTitle>Profile Information</CardTitle>
                <CardDescription>View and edit your personal information</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-start gap-4 p-4 bg-muted rounded-lg">
                  <div className="relative">
                    {user.profileImageUrl && !profileImageFailed ? (
                      <img
                        src={
                          user.profileImageUrl.startsWith('http')
                            ? user.profileImageUrl
                            : `${window.location.origin}${user.profileImageUrl}`
                        }
                        alt="Profile"
                        className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                        data-testid="profile-image"
                        onError={() => setProfileImageFailed(true)}
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-gray-300 flex items-center justify-center flex-shrink-0">
                        <User className="w-8 h-8 text-gray-500" />
                      </div>
                    )}
                    <label
                      htmlFor="profile-picture-upload"
                      className="absolute -bottom-1 -right-1 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center cursor-pointer hover:bg-primary/90 transition-colors"
                    >
                      <Camera className="w-3 h-3" />
                      <input
                        id="profile-picture-upload"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={e => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleProfilePictureUpload(file);
                          }
                        }}
                        disabled={uploadingProfilePicture}
                        data-testid="profile-picture-input"
                      />
                    </label>
                    {uploadingProfilePicture && (
                      <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                        <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                      </div>
                    )}
                  </div>
                  <div className="flex-grow">
                    <h3 className="font-semibold text-lg" data-testid="user-name">
                      {user.firstName} {user.lastName}
                    </h3>
                    <p className="text-muted-foreground mb-2" data-testid="user-email">
                      {user.email}
                    </p>

                    {user.phoneNumber && (
                      <div className="flex items-center gap-1 mb-1" data-testid="user-phone">
                        <Phone className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">
                          {formatPhoneNumber(user.phoneNumber)}
                        </span>
                      </div>
                    )}

                    {formatFullAddress(user) && (
                      <div className="flex items-start gap-1 mb-2" data-testid="user-address">
                        <MapPin className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-muted-foreground">
                          {formatFullAddress(user)}
                        </span>
                      </div>
                    )}

                    <Badge variant="secondary" data-testid="user-role">
                      {user.role}
                    </Badge>
                  </div>
                </div>

                {editingProfile ? (
                  <form onSubmit={handleProfileSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="firstName">First Name</Label>
                        <Input
                          id="firstName"
                          value={profileForm.firstName}
                          onChange={e =>
                            setProfileForm({ ...profileForm, firstName: e.target.value })
                          }
                          data-testid="input-first-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="lastName">Last Name</Label>
                        <Input
                          id="lastName"
                          value={profileForm.lastName}
                          onChange={e =>
                            setProfileForm({ ...profileForm, lastName: e.target.value })
                          }
                          data-testid="input-last-name"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={profileForm.email}
                        onChange={e => setProfileForm({ ...profileForm, email: e.target.value })}
                        data-testid="input-email"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phoneNumber">Phone Number</Label>
                      <Input
                        id="phoneNumber"
                        type="tel"
                        value={profileForm.phoneNumber}
                        onChange={e => {
                          const formatted = formatPhoneNumber(e.target.value);
                          setProfileForm({ ...profileForm, phoneNumber: formatted });
                        }}
                        placeholder="(555) 123-4567"
                        maxLength={14} // (XXX) XXX-XXXX = 14 characters
                        data-testid="input-phone-number"
                      />
                    </div>
                    <div>
                      <Label htmlFor="streetAddress">Street Address</Label>
                      <Input
                        id="streetAddress"
                        value={profileForm.streetAddress}
                        onChange={e =>
                          setProfileForm({ ...profileForm, streetAddress: e.target.value })
                        }
                        placeholder="123 Main St"
                        data-testid="input-street-address"
                      />
                    </div>
                    <div>
                      <Label htmlFor="addressLine2">Address Line 2 (Optional)</Label>
                      <Input
                        id="addressLine2"
                        value={profileForm.addressLine2}
                        onChange={e =>
                          setProfileForm({ ...profileForm, addressLine2: e.target.value })
                        }
                        placeholder="Apt 4B, Suite 200, etc."
                        data-testid="input-address-line-2"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="col-span-2">
                        <Label htmlFor="city">City</Label>
                        <Input
                          id="city"
                          value={profileForm.city}
                          onChange={e => setProfileForm({ ...profileForm, city: e.target.value })}
                          placeholder="Indianapolis"
                          data-testid="input-city"
                        />
                      </div>
                      <div>
                        <Label htmlFor="state">State</Label>
                        <Input
                          id="state"
                          value={profileForm.state}
                          onChange={e =>
                            setProfileForm({ ...profileForm, state: e.target.value.toUpperCase() })
                          }
                          placeholder="IN"
                          maxLength={2}
                          data-testid="input-state"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="zipCode">Zip Code</Label>
                        <Input
                          id="zipCode"
                          value={profileForm.zipCode}
                          onChange={e => {
                            const cleaned = e.target.value.replace(/\D/g, '');
                            setProfileForm({ ...profileForm, zipCode: cleaned });
                          }}
                          placeholder="46201"
                          maxLength={5}
                          data-testid="input-zip-code"
                        />
                      </div>
                      <div>
                        <Label htmlFor="country">Country</Label>
                        <Input
                          id="country"
                          value={profileForm.country}
                          onChange={e =>
                            setProfileForm({
                              ...profileForm,
                              country: e.target.value.toUpperCase(),
                            })
                          }
                          placeholder="US"
                          maxLength={2}
                          data-testid="input-country"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="timezone">Timezone</Label>
                        <Select
                          value={profileForm.timezone}
                          onValueChange={value =>
                            setProfileForm({ ...profileForm, timezone: value })
                          }
                        >
                          <SelectTrigger id="timezone" data-testid="input-timezone">
                            <SelectValue placeholder="Select your timezone" />
                          </SelectTrigger>
                          <SelectContent>
                            {US_TIMEZONES.map(tz => (
                              <SelectItem key={tz.value} value={tz.value}>
                                {tz.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center gap-2 mt-6">
                        <input
                          id="hasCompletedOnboarding"
                          type="checkbox"
                          checked={profileForm.hasCompletedOnboarding}
                          onChange={e =>
                            setProfileForm({
                              ...profileForm,
                              hasCompletedOnboarding: e.target.checked,
                            })
                          }
                          className="h-4 w-4"
                          data-testid="input-onboarding"
                        />
                        <Label htmlFor="hasCompletedOnboarding">I've completed onboarding</Label>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="submit"
                        disabled={updateProfileMutation.isPending}
                        data-testid="button-save-profile"
                      >
                        {updateProfileMutation.isPending ? 'Saving...' : 'Save Changes'}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setEditingProfile(false)}
                        data-testid="button-cancel-edit"
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                ) : (
                  <Button
                    onClick={() => {
                      setProfileForm({
                        firstName: user.firstName || '',
                        lastName: user.lastName || '',
                        email: user.email || '',
                        phoneNumber: formatPhoneNumber(user.phoneNumber || ''),
                        streetAddress: user.streetAddress || '',
                        addressLine2: user.addressLine2 || '',
                        city: user.city || '',
                        state: user.state || '',
                        zipCode: user.zipCode || '',
                        country: user.country || 'US',
                        timezone: user.timezone || 'UTC',
                        hasCompletedOnboarding: user.hasCompletedOnboarding || false,
                        emailEnrollmentConfirmation: user.emailEnrollmentConfirmation ?? true,
                        emailClassReminders: user.emailClassReminders ?? true,
                        emailCreatorUpdates: user.emailCreatorUpdates ?? true,
                        emailAccountNotifications: user.emailAccountNotifications ?? true,
                      });
                      setEditingProfile(true);
                    }}
                    data-testid="button-edit-profile"
                  >
                    Edit Profile
                  </Button>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="classes">
            <Card data-testid="orders-card">
              <CardHeader>
                <CardTitle>Your Classes</CardTitle>
                <CardDescription>
                  Track upcoming sessions, refund or cancellation activity, and past bookings.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {enrollmentsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
                  </div>
                ) : enrollments && Array.isArray(enrollments) && enrollments.length > 0 ? (
                  <div className="space-y-8">
                    <EnrollmentListSection
                      title="Upcoming classes"
                      description="These are the sessions still ahead of you."
                      emptyMessage="No upcoming classes are booked yet."
                      enrollments={enrollmentGroups.upcoming}
                      sessionTimezone={sessionTimezone}
                    />

                    <EnrollmentListSection
                      title="Refund and cancellation status"
                      description="Anything that needs review or follow-through appears here."
                      emptyMessage="No refund or cancellation issues right now."
                      enrollments={enrollmentGroups.attention}
                      sessionTimezone={sessionTimezone}
                    />

                    <EnrollmentListSection
                      title="Past classes"
                      description="Your previous bookings and completed sessions."
                      emptyMessage="Past classes will appear here after your first session."
                      enrollments={enrollmentGroups.past}
                      sessionTimezone={sessionTimezone}
                    />
                  </div>
                ) : (
                  <div className="text-center py-8" data-testid="no-enrollments">
                    <p className="text-muted-foreground">
                      You haven't enrolled in any classes yet.
                    </p>
                    <Button asChild className="mt-4">
                      <a href="/classes">Browse Classes</a>
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="passes">
            <Card>
              <CardHeader>
                <CardTitle>Punch Passes</CardTitle>
                <CardDescription>Active passes you can use to book sessions.</CardDescription>
              </CardHeader>
              <CardContent>
                {passesData?.passes && passesData.passes.length > 0 ? (
                  <div className="space-y-3">
                    {passesData.passes.map(pass => (
                      <div
                        key={pass.id}
                        className="flex items-start justify-between gap-4 rounded-lg border p-4"
                      >
                        <div className="min-w-0">
                          {pass.productName && (
                            <p className="text-sm font-semibold text-foreground">
                              {pass.productName}
                            </p>
                          )}
                          <p className="font-medium">
                            {pass.remainingSessions} of {pass.totalSessions} sessions remaining
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {pass.usedSessions} used
                            {pass.expiresAt
                              ? ` · Expires ${new Date(pass.expiresAt).toLocaleDateString()}`
                              : ' · No expiry'}
                          </p>
                          {pass.coveredClasses.length > 1 && (
                            <p className="mt-1 text-sm text-muted-foreground">
                              Good for:{' '}
                              {pass.coveredClasses.map((c, i) => (
                                <span key={c.id}>
                                  {i > 0 && ', '}
                                  <a
                                    href={`/classes/${c.id}`}
                                    className="text-primary hover:underline"
                                  >
                                    {c.title}
                                  </a>
                                </span>
                              ))}
                            </p>
                          )}
                        </div>
                        <div className="flex shrink-0 flex-col items-end gap-2">
                          {pass.coveredClasses.length <= 1 && (
                            <a
                              href={`/classes/${pass.coveredClasses[0]?.id ?? pass.classId}`}
                              className="text-sm text-primary hover:underline"
                            >
                              View class
                            </a>
                          )}
                          {pass.remainingSessions > 0 && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setSendingFromPass(pass)}
                              data-testid={`button-send-punches-${pass.id}`}
                            >
                              Send punches
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">
                    You don&apos;t have any active punch passes.
                  </p>
                )}
                <div className="mt-6">
                  <SentTransfersList />
                </div>
                {sendingFromPass && (
                  <SendPunchesDialog
                    passId={sendingFromPass.id}
                    passName={sendingFromPass.productName ?? 'punch pass'}
                    remaining={sendingFromPass.remainingSessions}
                    open
                    onOpenChange={open => !open && setSendingFromPass(null)}
                  />
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settings">
            <div className="space-y-6">
              <Card data-testid="password-reset-card">
                <CardHeader>
                  <CardTitle>Password & Security</CardTitle>
                  <CardDescription>
                    Manage your account security and password settings
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {isEmailPasswordUser ? (
                    // Email/Password Authentication
                    <div className="space-y-4">
                      <div className="p-4 border rounded-lg">
                        <h4 className="font-medium mb-2">Change Password</h4>
                        <p className="text-sm text-muted-foreground mb-4">
                          Update your account password to keep your account secure.
                        </p>

                        {!changingPassword ? (
                          <Button
                            variant="outline"
                            onClick={() => setChangingPassword(true)}
                            data-testid="button-change-password"
                          >
                            Change Password
                          </Button>
                        ) : (
                          <form onSubmit={handlePasswordSubmit} className="space-y-4">
                            <div>
                              <Label htmlFor="currentPassword">Current Password</Label>
                              <Input
                                id="currentPassword"
                                type="password"
                                value={passwordForm.currentPassword}
                                onChange={e =>
                                  setPasswordForm({
                                    ...passwordForm,
                                    currentPassword: e.target.value,
                                  })
                                }
                                required
                                data-testid="input-current-password"
                              />
                            </div>
                            <div>
                              <Label htmlFor="newPassword">New Password</Label>
                              <Input
                                id="newPassword"
                                type="password"
                                value={passwordForm.newPassword}
                                onChange={e =>
                                  setPasswordForm({ ...passwordForm, newPassword: e.target.value })
                                }
                                minLength={8}
                                required
                                data-testid="input-new-password"
                              />
                              <p className="mt-1 text-xs text-muted-foreground">
                                At least 8 characters with an uppercase letter, a lowercase letter,
                                a number, and a special character.
                              </p>
                            </div>
                            <div>
                              <Label htmlFor="confirmPassword">Confirm New Password</Label>
                              <Input
                                id="confirmPassword"
                                type="password"
                                value={passwordForm.confirmPassword}
                                onChange={e =>
                                  setPasswordForm({
                                    ...passwordForm,
                                    confirmPassword: e.target.value,
                                  })
                                }
                                minLength={8}
                                required
                                data-testid="input-confirm-password"
                              />
                            </div>
                            <div className="flex gap-2">
                              <Button
                                type="submit"
                                disabled={changePasswordMutation.isPending}
                                data-testid="button-save-password"
                              >
                                {changePasswordMutation.isPending
                                  ? 'Changing...'
                                  : 'Change Password'}
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                  setChangingPassword(false);
                                  setPasswordForm({
                                    currentPassword: '',
                                    newPassword: '',
                                    confirmPassword: '',
                                  });
                                }}
                                data-testid="button-cancel-password"
                              >
                                Cancel
                              </Button>
                            </div>
                          </form>
                        )}
                      </div>
                    </div>
                  ) : (
                    // Google OAuth Authentication
                    <div className="space-y-4">
                      <div className="p-4 border rounded-lg bg-muted/50">
                        <h4 className="font-medium mb-2">Password Reset</h4>
                        <p className="text-sm text-muted-foreground mb-4">
                          Since you're using Google OAuth to sign in, your password is managed by
                          Google. To reset your password, visit your Google Account settings.
                        </p>
                        <Button
                          variant="outline"
                          onClick={() =>
                            window.open('https://myaccount.google.com/security', '_blank')
                          }
                          data-testid="button-google-security"
                        >
                          Go to Google Account Security
                        </Button>
                      </div>

                      <div className="p-4 border rounded-lg">
                        <h4 className="font-medium mb-2">Two-Factor Authentication</h4>
                        <p className="text-sm text-muted-foreground mb-4">
                          For enhanced security, enable two-factor authentication on your Google
                          account.
                        </p>
                        <Button
                          variant="outline"
                          onClick={() =>
                            window.open(
                              'https://myaccount.google.com/signinoptions/two-step-verification',
                              '_blank'
                            )
                          }
                          data-testid="button-google-2fa"
                        >
                          Manage 2FA Settings
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card data-testid="email-preferences-card">
                <CardHeader>
                  <CardTitle>Email Preferences</CardTitle>
                  <CardDescription>
                    Choose which class, creator, and account emails you want to receive.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div className="flex items-center justify-between gap-4">
                    <div className="space-y-1">
                      <Label htmlFor="settings-emailEnrollmentConfirmation">
                        Enrollment confirmations
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Send me an email after I successfully book a class.
                      </p>
                    </div>
                    <Switch
                      id="settings-emailEnrollmentConfirmation"
                      checked={profileForm.emailEnrollmentConfirmation}
                      onCheckedChange={checked =>
                        setProfileForm({
                          ...profileForm,
                          emailEnrollmentConfirmation: checked,
                        })
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <div className="space-y-1">
                      <Label htmlFor="settings-emailClassReminders">Class reminders</Label>
                      <p className="text-sm text-muted-foreground">
                        Send me reminder emails before my upcoming sessions.
                      </p>
                    </div>
                    <Switch
                      id="settings-emailClassReminders"
                      checked={profileForm.emailClassReminders}
                      onCheckedChange={checked =>
                        setProfileForm({
                          ...profileForm,
                          emailClassReminders: checked,
                        })
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <div className="space-y-1">
                      <Label htmlFor="settings-emailCreatorUpdates">Creator updates</Label>
                      <p className="text-sm text-muted-foreground">
                        Send me new-class and creator update emails.
                      </p>
                    </div>
                    <Switch
                      id="settings-emailCreatorUpdates"
                      checked={profileForm.emailCreatorUpdates}
                      onCheckedChange={checked =>
                        setProfileForm({
                          ...profileForm,
                          emailCreatorUpdates: checked,
                        })
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <div className="space-y-1">
                      <Label htmlFor="settings-emailAccountNotifications">
                        Account notifications
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Send important payment, refund, and security account emails.
                      </p>
                    </div>
                    <Switch
                      id="settings-emailAccountNotifications"
                      checked={profileForm.emailAccountNotifications}
                      onCheckedChange={checked =>
                        setProfileForm({
                          ...profileForm,
                          emailAccountNotifications: checked,
                        })
                      }
                    />
                  </div>
                  <Button
                    type="button"
                    onClick={() => updateEmailPreferencesMutation.mutate()}
                    disabled={updateEmailPreferencesMutation.isPending}
                    data-testid="button-save-email-preferences"
                  >
                    {updateEmailPreferencesMutation.isPending ? 'Saving...' : 'Save Email Settings'}
                  </Button>
                </CardContent>
              </Card>

              <Card data-testid="settings-card">
                <CardHeader>
                  <CardTitle>Account Settings</CardTitle>
                  <CardDescription>Manage your account preferences and security</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-semibold mb-2">Account Type</h3>
                      <Badge variant="outline" data-testid="account-type">
                        {user.role === 'user'
                          ? 'Standard User'
                          : user.role === 'creator'
                            ? 'Creator Account'
                            : 'Administrator'}
                      </Badge>
                    </div>

                    {user.role === 'user' && (
                      <div className="pt-2 border-t">
                        <h3 className="font-semibold mb-1">Become a Creator</h3>
                        {!creatorApplication && !showApplyForm && (
                          <>
                            <p className="text-sm text-muted-foreground mb-3">
                              Share your expertise by hosting classes on Kalovate.
                            </p>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setShowApplyForm(true)}
                              className="flex items-center gap-2"
                            >
                              <Star size={14} />
                              Apply to become a creator
                            </Button>
                          </>
                        )}
                        {creatorApplication && creatorApplication.status === 'pending' && (
                          <p className="text-sm text-muted-foreground">
                            Your application is under review. We'll notify you when it's processed.
                          </p>
                        )}
                        {creatorApplication && creatorApplication.status === 'rejected' && (
                          <p className="text-sm text-destructive">
                            Your previous application was not approved.{' '}
                            <button
                              type="button"
                              className="underline"
                              onClick={() => {
                                setShowApplyForm(true);
                              }}
                            >
                              Apply again
                            </button>
                          </p>
                        )}
                        {showApplyForm && (
                          <form
                            className="space-y-3 mt-3"
                            onSubmit={e => {
                              e.preventDefault();
                              submitApplicationMutation.mutate(applyForm);
                            }}
                          >
                            <div>
                              <Label htmlFor="apply-display-name">Display name *</Label>
                              <Input
                                id="apply-display-name"
                                value={applyForm.displayName}
                                onChange={e =>
                                  setApplyForm(f => ({ ...f, displayName: e.target.value }))
                                }
                                required
                              />
                            </div>
                            <div>
                              <Label htmlFor="apply-phone-number">Phone number *</Label>
                              <Input
                                id="apply-phone-number"
                                type="tel"
                                value={applyForm.phoneNumber}
                                onChange={e =>
                                  setApplyForm(f => ({ ...f, phoneNumber: e.target.value }))
                                }
                                placeholder="Best number for application follow-up"
                                required
                              />
                            </div>
                            <div>
                              <Label htmlFor="apply-bio">Short bio</Label>
                              <Textarea
                                id="apply-bio"
                                value={applyForm.bio}
                                onChange={e => setApplyForm(f => ({ ...f, bio: e.target.value }))}
                                rows={6}
                              />
                            </div>
                            <div>
                              <Label htmlFor="apply-creator-headline">Creator headline</Label>
                              <Input
                                id="apply-creator-headline"
                                value={applyForm.creatorHeadline}
                                onChange={e =>
                                  setApplyForm(f => ({
                                    ...f,
                                    creatorHeadline: e.target.value,
                                  }))
                                }
                                placeholder="e.g., Coffee Education, Portrait Photography"
                              />
                            </div>
                            <div>
                              <Label htmlFor="apply-portfolio">Portfolio or website URL</Label>
                              <Input
                                id="apply-portfolio"
                                type="url"
                                value={applyForm.portfolioUrl}
                                onChange={e =>
                                  setApplyForm(f => ({ ...f, portfolioUrl: e.target.value }))
                                }
                              />
                            </div>
                            <div>
                              <Label htmlFor="apply-experience">Experience</Label>
                              <Input
                                id="apply-experience"
                                value={applyForm.experience}
                                onChange={e =>
                                  setApplyForm(f => ({ ...f, experience: e.target.value }))
                                }
                              />
                            </div>
                            <div>
                              <Label htmlFor="apply-proposed-content">
                                What would you teach? *
                              </Label>
                              <Textarea
                                id="apply-proposed-content"
                                value={applyForm.proposedContent}
                                onChange={e =>
                                  setApplyForm(f => ({ ...f, proposedContent: e.target.value }))
                                }
                                placeholder="Describe the classes or workshops you'd like to offer..."
                                rows={3}
                                required
                              />
                            </div>
                            <div>
                              <Label htmlFor="apply-experience-level">Experience level *</Label>
                              <select
                                id="apply-experience-level"
                                aria-label="Experience level"
                                value={applyForm.experienceLevel}
                                onChange={e =>
                                  setApplyForm(f => ({ ...f, experienceLevel: e.target.value }))
                                }
                                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                required
                              >
                                <option value="">Select…</option>
                                <option value="hobbyist">Hobbyist</option>
                                <option value="semi_professional">Semi-professional</option>
                                <option value="professional">Professional</option>
                                <option value="expert">Expert</option>
                              </select>
                            </div>
                            <div className="space-y-2">
                              <Label>Existing audience?</Label>
                              <div className="flex items-center gap-3">
                                <button
                                  type="button"
                                  onClick={() =>
                                    setApplyForm(f => ({
                                      ...f,
                                      hasExistingAudience: !f.hasExistingAudience,
                                    }))
                                  }
                                  className={`rounded-full px-4 py-1.5 text-sm border ${applyForm.hasExistingAudience ? 'bg-foreground text-background' : 'border-border bg-card text-foreground'}`}
                                >
                                  {applyForm.hasExistingAudience ? 'Yes' : 'No'}
                                </button>
                                <span className="text-sm text-muted-foreground">
                                  Do you have an existing following or community?
                                </span>
                              </div>
                              {applyForm.hasExistingAudience && (
                                <Textarea
                                  value={applyForm.audienceDescription}
                                  onChange={e =>
                                    setApplyForm(f => ({
                                      ...f,
                                      audienceDescription: e.target.value,
                                    }))
                                  }
                                  placeholder="Describe your audience, platform, or community size..."
                                  rows={2}
                                />
                              )}
                            </div>
                            <div>
                              <Label htmlFor="apply-primary-location">
                                Primary city or region you'd teach in
                              </Label>
                              <Input
                                id="apply-primary-location"
                                value={applyForm.primaryLocation}
                                onChange={e =>
                                  setApplyForm(f => ({ ...f, primaryLocation: e.target.value }))
                                }
                                placeholder="e.g., Bloomington, IN or Online"
                              />
                            </div>
                            <div>
                              <Label htmlFor="apply-referral-source">
                                How did you hear about Kalovate?
                              </Label>
                              <select
                                id="apply-referral-source"
                                aria-label="How did you hear about Kalovate"
                                value={applyForm.referralSource}
                                onChange={e =>
                                  setApplyForm(f => ({ ...f, referralSource: e.target.value }))
                                }
                                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                              >
                                <option value="">Prefer not to say</option>
                                <option value="friend_referral">A friend or colleague</option>
                                <option value="social_media">Social media</option>
                                <option value="search_engine">Search engine</option>
                                <option value="local_event">Local event or community</option>
                                <option value="other">Other</option>
                              </select>
                            </div>
                            <div className="flex gap-2 pt-1">
                              <Button
                                type="submit"
                                size="sm"
                                disabled={submitApplicationMutation.isPending}
                              >
                                {submitApplicationMutation.isPending
                                  ? 'Submitting…'
                                  : 'Submit Application'}
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => setShowApplyForm(false)}
                              >
                                Cancel
                              </Button>
                            </div>
                          </form>
                        )}
                      </div>
                    )}

                    <div>
                      <h3 className="font-semibold mb-2">Account Status</h3>
                      <Badge
                        variant={user?.isActive ? 'default' : 'destructive'}
                        data-testid="account-status"
                      >
                        {user?.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>

                    <div className="pt-4 border-t">
                      <Button
                        variant="destructive"
                        onClick={handleLogout}
                        className="flex items-center gap-2"
                        data-testid="button-logout"
                      >
                        <LogOut size={16} />
                        Sign Out
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {user.isBlogWriter && (
            <TabsContent value="writing">
              <Card>
                <CardContent className="pt-6">
                  <WriterPortal />
                </CardContent>
              </Card>
            </TabsContent>
          )}

          <TabsContent value="calendar">
            <MyCalendarTab items={myCalendar} isLoading={calendarLoading} />
          </TabsContent>
        </Tabs>
      </div>
    </PublicShell>
  );
}

function AccountOverviewCard({
  icon: Icon,
  label,
  value,
  detail,
}: {
  icon: typeof CreditCard;
  label: string;
  value: string;
  detail: string;
}) {
  return (
    <Card className="rounded-[1.5rem] border-border/70 shadow-sm">
      <CardContent className="space-y-3 p-5">
        <Icon className="h-5 w-5 text-primary" />
        <div>
          <p className="text-2xl font-semibold text-foreground">{value}</p>
          <p className="text-sm font-medium text-foreground/80">{label}</p>
        </div>
        <p className="text-sm leading-6 text-muted-foreground">{detail}</p>
      </CardContent>
    </Card>
  );
}

function EnrollmentListSection({
  title,
  description,
  emptyMessage,
  enrollments,
  sessionTimezone,
}: {
  title: string;
  description: string;
  emptyMessage: string;
  enrollments: EnrollmentData[];
  sessionTimezone: string;
}) {
  return (
    <section className="space-y-4">
      <div>
        <h3 className="text-xl font-semibold text-foreground">{title}</h3>
        <p className="mt-1 text-sm leading-6 text-muted-foreground">{description}</p>
      </div>

      {enrollments.length > 0 ? (
        <div className="space-y-4">
          {enrollments.map(enrollment => (
            <EnrollmentRecordCard
              key={enrollment.id}
              enrollment={enrollment}
              sessionTimezone={sessionTimezone}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-[1.25rem] border border-dashed border-border/70 bg-muted/20 p-5 text-sm text-muted-foreground">
          {emptyMessage}
        </div>
      )}
    </section>
  );
}

function EnrollmentRecordCard({
  enrollment,
  sessionTimezone,
}: {
  enrollment: EnrollmentData;
  sessionTimezone: string;
}) {
  const { toast } = useToast();
  const [refundReason, setRefundReason] = useState('');
  const [showRefundForm, setShowRefundForm] = useState(false);
  const [reminderEnabled, setReminderEnabled] = useState(enrollment.reminderEnabled ?? true);

  const reminderMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const res = await apiRequest('PATCH', `/api/enrollments/${enrollment.id}/reminder`, {
        enabled,
      });
      return res.json();
    },
    onSuccess: (_data, enabled) => {
      setReminderEnabled(enabled);
      toast({
        title: enabled ? 'Reminder on' : 'Reminder off',
        description: enabled
          ? "You'll get a reminder 24 hours before this session."
          : 'Reminder email turned off for this session.',
      });
      queryClient.invalidateQueries({ queryKey: ['/api/enrollments'] });
    },
    onError: () => {
      toast({ title: 'Failed to update reminder', variant: 'destructive' });
    },
  });

  const requestRefundMutation = useMutation({
    mutationFn: async ({ orderId, reason }: { orderId: string; reason?: string }) => {
      const response = await apiRequest('POST', '/api/refunds/requests', { orderId, reason });
      return await response.json();
    },
    onSuccess: data => {
      toast({
        title: data.autoApproved ? 'Refund Approved' : 'Refund Requested',
        description: data.autoApproved
          ? 'Your refund has been automatically approved.'
          : 'Your refund request has been submitted for review.',
      });
      setShowRefundForm(false);
      setRefundReason('');
      queryClient.setQueryData<EnrollmentData[]>(['/api/enrollments'], old =>
        old
          ? old.map(e =>
              e.id === enrollment.id
                ? {
                    ...e,
                    ...(data.autoApproved
                      ? { status: 'refunded' }
                      : { refundRequestStatus: 'pending' }),
                  }
                : e
            )
          : old
      );
      void queryClient.invalidateQueries({ queryKey: ['/api/enrollments'] });
    },
    onError: (error: any) => {
      toast({
        title: 'Request Failed',
        description: error?.message || 'Failed to submit refund request.',
        variant: 'destructive',
      });
    },
  });

  // Pass-redeemed enrollments cancel immediately (return the punch, free the
  // seat) rather than going through the order refund flow.
  const cancelPassMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest(
        'POST',
        `/api/classes/${enrollment.classId}/sessions/${enrollment.sessionId}/cancel-pass`
      );
      return await response.json();
    },
    onSuccess: data => {
      toast({
        title: 'Session canceled',
        description:
          data?.remainingSessions != null
            ? `Your punch was returned — ${data.remainingSessions} left on your pass.`
            : 'Your punch was returned to your pass.',
      });
      queryClient.setQueryData<EnrollmentData[]>(['/api/enrollments'], old =>
        old ? old.map(e => (e.id === enrollment.id ? { ...e, status: 'canceled' } : e)) : old
      );
      void queryClient.invalidateQueries({ queryKey: ['/api/enrollments'] });
      void queryClient.invalidateQueries({ queryKey: ['/api/classes/user/passes'] });
    },
    onError: (error: any) => {
      toast({
        title: 'Cancellation Failed',
        description: error?.message || 'Failed to cancel this pass session.',
        variant: 'destructive',
      });
    },
  });

  // The enrollments API normalizes healthy order-backed enrollments to status
  // 'confirmed' (refunds/cancellations get their own statuses).
  const canRequestCancellation = canRequestEnrollmentCancellation(enrollment);
  const canCancelPass = canCancelPassEnrollment(enrollment);
  // After the cancellation window (1 day past the class) the student emails the
  // instructor directly for any issues instead of self-serve cancellation.
  const canEmailTeacher = shouldEmailTeacherForIssue(enrollment);
  const emailTeacherHref = enrollment.creatorEmail
    ? `mailto:${enrollment.creatorEmail}?subject=${encodeURIComponent(
        `Question about ${enrollment.classTitle ?? 'my class'}`
      )}`
    : null;

  const isUpcomingSession =
    enrollment.sessionStartDateTime != null &&
    new Date(enrollment.sessionStartDateTime) > new Date() &&
    enrollment.status !== 'canceled' &&
    enrollment.status !== 'refunded';

  return (
    <div
      className="rounded-[1.5rem] border border-border/70 bg-card p-5 shadow-sm"
      data-testid={`enrollment-${enrollment.id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3">
            {enrollment.classThumbnailUrl ? (
              <img
                src={
                  enrollment.classThumbnailUrl.startsWith('http')
                    ? enrollment.classThumbnailUrl
                    : `${window.location.origin}${enrollment.classThumbnailUrl}`
                }
                alt={enrollment.classTitle || 'Class thumbnail'}
                className="h-12 w-12 rounded-lg object-cover"
                onError={e => {
                  const target = e.target as HTMLImageElement;
                  target.src =
                    'https://images.unsplash.com/photo-1578662996442-48f60103fc96?ixlib=rb-4.0.3&auto=format&fit=crop&w=96&h=96';
                }}
              />
            ) : null}
            <div className="min-w-0">
              <h4 className="truncate text-lg font-semibold text-foreground">
                {enrollment.classTitle || 'Untitled Class'}
              </h4>
              <p className="text-sm text-muted-foreground">
                by {enrollment.creatorName || 'Unknown Creator'}
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2">
          <Badge
            variant={enrollment.status === 'confirmed' ? 'default' : 'secondary'}
            data-testid={`status-${enrollment.id}`}
          >
            {formatEnrollmentStatusLabel(enrollment.status)}
          </Badge>
          {(enrollment.orderTotal || 0) === 0 ? (
            <Badge variant="outline" className="border-green-600 text-green-600">
              FREE
            </Badge>
          ) : null}
        </div>
      </div>

      <div className="mt-4 grid gap-4 rounded-[1.25rem] bg-muted/30 p-4 md:grid-cols-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Purchase date
          </p>
          <p
            className="mt-1 font-semibold text-foreground"
            data-testid={`purchase-date-${enrollment.id}`}
          >
            {formatDateCompact(
              enrollment.purchaseDate || enrollment.enrolledAt || new Date().toISOString()
            )}
          </p>
        </div>
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Tickets
          </p>
          <p
            className="mt-1 font-semibold text-foreground"
            data-testid={`quantity-${enrollment.id}`}
          >
            {enrollment.quantity || 1} {(enrollment.quantity || 1) === 1 ? 'ticket' : 'tickets'}
          </p>
        </div>
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Total</p>
          <p className="mt-1 font-semibold text-foreground" data-testid={`price-${enrollment.id}`}>
            {(enrollment.orderTotal || 0) === 0
              ? 'FREE'
              : `$${(enrollment.orderTotal || 0).toFixed(2)}`}
          </p>
        </div>
      </div>

      {enrollment.sessionStartDateTime ? (
        <div className="mt-4 rounded-[1.25rem] border border-blue-200 bg-blue-50 p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-blue-700">Session</p>
          <p className="mt-1 text-sm text-blue-900" data-testid={`session-date-${enrollment.id}`}>
            {formatSessionTime(
              new Date(enrollment.sessionStartDateTime),
              new Date(enrollment.sessionEndDateTime ?? enrollment.sessionStartDateTime),
              sessionTimezone
            )}
          </p>
          {enrollment.sessionNotes ? (
            <p className="mt-2 text-sm text-blue-900">{enrollment.sessionNotes}</p>
          ) : null}
        </div>
      ) : null}

      {enrollment.refundRequestStatus || enrollment.refundDeniedReason ? (
        <div className="mt-4 rounded-[1.25rem] border border-amber-300/70 bg-amber-50/80 p-4 text-sm text-amber-900">
          <p className="font-medium">
            Refund status:{' '}
            {formatEnrollmentStatusLabel(enrollment.status || enrollment.refundRequestStatus)}
          </p>
          {enrollment.refundDeniedReason ? (
            <p className="mt-1 text-amber-800">{enrollment.refundDeniedReason}</p>
          ) : null}
        </div>
      ) : null}

      {canRequestCancellation && !showRefundForm && (
        <div className="mt-4">
          <Button
            size="sm"
            variant="outline"
            className="w-full sm:w-auto"
            onClick={() => setShowRefundForm(true)}
            data-testid={`button-request-refund-${enrollment.id}`}
          >
            Request cancellation
          </Button>
        </div>
      )}

      {canCancelPass && (
        <div className="mt-4">
          <p className="mb-2 text-xs text-muted-foreground">
            Canceling returns this punch to your pass and frees your spot.
          </p>
          <Button
            size="sm"
            variant="outline"
            className="w-full sm:w-auto"
            disabled={cancelPassMutation.isPending}
            onClick={() => cancelPassMutation.mutate()}
            data-testid={`button-cancel-pass-${enrollment.id}`}
          >
            {cancelPassMutation.isPending ? 'Canceling…' : 'Cancel session'}
          </Button>
        </div>
      )}

      {canRequestCancellation && showRefundForm && (
        <div className="mt-4 space-y-3 rounded-[1.25rem] border border-border/70 bg-muted/30 p-4">
          <p className="text-sm font-medium">Request cancellation</p>
          <p className="text-xs text-muted-foreground">
            {enrollment.orderTotal != null && enrollment.orderTotal > 0 ? (
              <>
                Your request is reviewed by the instructor. Within the class&apos;s
                cancellation-policy window it will be refunded; afterward it&apos;s at their
                discretion. Refunds are the amount you paid, sometimes minus the card processing fee
                (about 3%), which is non-refundable depending on the circumstances.
              </>
            ) : (
              <>
                Your request is reviewed by the instructor to release your spot. This is a free
                class, so there&apos;s no payment to refund.
              </>
            )}
          </p>
          <textarea
            className="w-full rounded-xl border border-border/60 bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            rows={3}
            placeholder="Reason for cancellation (optional)"
            value={refundReason}
            onChange={e => setRefundReason(e.target.value)}
            data-testid={`input-refund-reason-${enrollment.id}`}
          />
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              size="sm"
              className="w-full sm:w-auto"
              onClick={() => {
                if (!enrollment.orderId) return;
                requestRefundMutation.mutate({
                  orderId: enrollment.orderId,
                  reason: refundReason || undefined,
                });
              }}
              disabled={requestRefundMutation.isPending}
              data-testid={`button-submit-refund-${enrollment.id}`}
            >
              {requestRefundMutation.isPending ? 'Submitting...' : 'Submit request'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="w-full sm:w-auto"
              onClick={() => {
                setShowRefundForm(false);
                setRefundReason('');
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {canEmailTeacher && (
        <div className="mt-4">
          <p className="mb-2 text-xs text-muted-foreground">
            The cancellation window for this class has passed. For any issues, contact the
            instructor directly.
          </p>
          {emailTeacherHref ? (
            <Button
              asChild
              size="sm"
              variant="outline"
              className="w-full sm:w-auto"
              data-testid={`button-email-teacher-${enrollment.id}`}
            >
              <a href={emailTeacherHref}>Email teacher</a>
            </Button>
          ) : (
            <p className="text-xs text-muted-foreground">
              Instructor contact isn&apos;t available for this class.
            </p>
          )}
        </div>
      )}

      {isUpcomingSession && (
        <div className="mt-4 flex items-center justify-between rounded-[1.25rem] border border-border/60 bg-muted/20 px-4 py-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Bell className="h-4 w-4 shrink-0" />
            <span>24-hour reminder email</span>
          </div>
          <Switch
            checked={reminderEnabled}
            onCheckedChange={enabled => reminderMutation.mutate(enabled)}
            disabled={reminderMutation.isPending}
            aria-label="Toggle session reminder email"
          />
        </div>
      )}

      {enrollment.classDescription ? (
        <p className="mt-4 line-clamp-2 text-sm leading-6 text-muted-foreground">
          {enrollment.classDescription}
        </p>
      ) : null}
    </div>
  );
}

// ─── My Calendar Tab ─────────────────────────────────────────────────────────

type MyCalendarItem = {
  type: 'enrollment' | 'going' | 'interested' | 'saved';
  id: string;
  classId?: string;
  sessionId?: string;
  title: string;
  creatorName: string | null;
  startsAt: string;
  endsAt: string | null;
  status?: string;
  pricingMode?: string;
  deliveryMode?: string;
  category?: string;
  externalUrl?: string | null;
  attendanceMode?: string;
  location: string | null;
};

function MyCalendarTab({ items, isLoading }: { items: MyCalendarItem[]; isLoading: boolean }) {
  const [filter, setFilter] = useState<'upcoming' | 'past'>('upcoming');
  const now = new Date();

  const filtered = items.filter(item => {
    const starts = new Date(item.startsAt);
    return filter === 'upcoming' ? starts >= now : starts < now;
  });

  // Group by date string
  const grouped = filtered.reduce<Record<string, MyCalendarItem[]>>((acc, item) => {
    const day = new Date(item.startsAt).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
    (acc[day] ??= []).push(item);
    return acc;
  }, {});

  const typeConfig = {
    enrollment: { label: 'Enrolled', color: 'bg-primary/10 text-primary', icon: BookOpen },
    going: { label: 'Going', color: 'bg-green-100 text-green-700', icon: CalendarDays },
    interested: { label: 'Interested', color: 'bg-amber-100 text-amber-700', icon: CalendarDays },
    saved: { label: 'Saved', color: 'bg-muted text-muted-foreground', icon: Bookmark },
  } as const;

  return (
    <div className="space-y-6">
      {/* Filter toggle */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setFilter('upcoming')}
          className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${filter === 'upcoming' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`}
        >
          Upcoming
        </button>
        <button
          type="button"
          onClick={() => setFilter('past')}
          className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${filter === 'past' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`}
        >
          Past
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border/70 p-10 text-center">
          <CalendarDays className="mx-auto mb-3 h-8 w-8 text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">
            {filter === 'upcoming'
              ? 'No upcoming events. Enroll in a class or RSVP to a community event to see it here.'
              : 'No past events.'}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([day, dayItems]) => (
            <div key={day}>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {day}
              </p>
              <div className="space-y-2">
                {dayItems.map(item => {
                  const cfg = typeConfig[item.type];
                  const Icon = cfg.icon;
                  const startTime = new Date(item.startsAt).toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                  });
                  const endTime = item.endsAt
                    ? new Date(item.endsAt).toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                      })
                    : null;

                  const inner = (
                    <div className="flex items-start gap-3 rounded-xl border border-border/70 bg-card px-4 py-3 hover:bg-muted/30 transition-colors">
                      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm truncate">{item.title}</span>
                          <span
                            className={`rounded-full px-2 py-0.5 text-[10px] font-semibold leading-none ${cfg.color}`}
                          >
                            {cfg.label}
                          </span>
                        </div>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {startTime}
                          {endTime ? ` – ${endTime}` : ''}
                          {item.creatorName ? ` · ${item.creatorName}` : ''}
                          {item.location
                            ? ` · ${item.location}`
                            : item.attendanceMode === 'online'
                              ? ' · Online'
                              : ''}
                        </p>
                      </div>
                    </div>
                  );

                  if (item.type === 'enrollment' && item.classId) {
                    return (
                      <Link key={item.id} href={`/classes/${item.classId}`}>
                        {inner}
                      </Link>
                    );
                  }
                  if (item.externalUrl) {
                    return (
                      <a
                        key={item.id}
                        href={item.externalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {inner}
                      </a>
                    );
                  }
                  return <div key={item.id}>{inner}</div>;
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
